/**
 * Shared scan-COVERAGE parsing for the RAG advisory.
 *
 * A scanner cannot prove the absence of a threat, so a "no threats detected" GREEN is only
 * honest when coverage was complete. This reads the scanner's structured `coverage` block from
 * the analytics blob when present; otherwise it falls back to inferring coverage gaps from
 * finding-title notices ("File not fully scanned", scan timeouts, Heuristics.Limits.Exceeded) so
 * older scan blobs still downgrade a confident green.
 *
 * Pure/​defensive — never throws; unknown shapes resolve to "no gaps known" (source: 'none').
 */

export interface ScanVolume {
    device?: string | null
    fs?: string | null
    scanned?: boolean | null
    skip_reason?: string | null
}

export interface ScanCoverage {
    total: number | null       // volumes_total
    scanned: number | null     // volumes_scanned
    skipped: number            // volumes_skipped (0 when unknown)
    volumes: ScanVolume[]      // per-volume detail (device / fs / scanned / skip_reason)
    notes: string[]            // coverage notes (gaps, not threats)
    incomplete: boolean        // true => do NOT show a confident green
    source: 'block' | 'inferred' | 'none'
}

/** A finding that flags a coverage gap (a file/volume wasn't fully inspected) — NOT a threat. */
export function isCoverageNotice(f: any): boolean {
    if (!f) return false
    if (String(f.title || '').startsWith('File not fully scanned')) return true
    if (f.details?.reason === 'scan_timeout') return true
    const sig = String(f.details?.signature || f.classification || '')
    if (/^Heuristics\.Limits\.Exceeded/i.test(sig)) return true
    return false
}

function toInt(v: any): number | null {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
}

function toNotes(v: any): string[] {
    if (Array.isArray(v)) return v.map((x) => String(x)).filter(Boolean)
    if (typeof v === 'string' && v.trim()) return [v.trim()]
    return []
}

/**
 * Parse the coverage picture from a parsed analytics blob (`data`).
 */
export function parseScanCoverage(data: any): ScanCoverage {
    const cov = data?.coverage
    if (cov && typeof cov === 'object') {
        const total = toInt(cov.volumes_total)
        const scanned = toInt(cov.volumes_scanned)
        const skipped = toInt(cov.volumes_skipped) ?? 0
        const volumes: ScanVolume[] = Array.isArray(cov.volumes)
            ? cov.volumes
            : Array.isArray(cov.per_volume)
                ? cov.per_volume
                : []
        const notes = toNotes(cov.notes)
        const incomplete =
            skipped > 0 ||
            notes.length > 0 ||
            (total != null && scanned != null && scanned < total) ||
            volumes.some((v) => v && v.scanned === false)
        return { total, scanned, skipped, volumes, notes, incomplete, source: 'block' }
    }

    // Fallback: infer coverage gaps from finding-title notices (older blobs with no block).
    const findings: any[] = Array.isArray(data?.findings)
        ? data.findings
        : Array.isArray(data?.top_findings)
            ? data.top_findings
            : []
    const notices = findings.filter(isCoverageNotice)
    if (notices.length > 0) {
        return {
            total: null,
            scanned: null,
            skipped: notices.length,
            volumes: [],
            notes: notices.map((f) => String(f.title || 'File not fully scanned')).slice(0, 8),
            incomplete: true,
            source: 'inferred',
        }
    }

    return { total: null, scanned: null, skipped: 0, volumes: [], notes: [], incomplete: false, source: 'none' }
}

/**
 * How each file the scanner read was tied to the file the copy task wrote (`copy_check`, on
 * scans from 7 Oct 2026): by its checksum, or - weaker - by its version and size.
 */
export interface CopyCheck {
    byHash: number
    byVersionAndSize: number
}

export function parseCopyCheck(data: any): CopyCheck | null {
    const check = data?.copy_check
    if (!check || typeof check !== 'object') return null
    const byHash = toInt(check.by_hash)
    const byVersionAndSize = toInt(check.by_version_and_size)
    if (byHash == null && byVersionAndSize == null) return null
    return { byHash: byHash ?? 0, byVersionAndSize: byVersionAndSize ?? 0 }
}

/**
 * A database backup file and how much of it the scanner read (database scans from 8 Oct 2026).
 *
 * Two blocks on a coverage row say so, and a file can carry either or both (the Scanner
 * session, 8 Oct 2026):
 *   measured      the raw bytes were measured (SQL Server .bak, Parquet, RDB, bacpac, Data Pump,
 *                 anything unknown, text over 1 GB).
 *   format_check  the file was checked against its format. A MySQL / PostgreSQL dump (.sql.zst)
 *                 and DynamoDB items have ONLY this: their stored bytes are compressed, so the
 *                 check reads every byte and judges the decoded content instead.
 * `measured` answers for the bytes when it is there; otherwise `format_check` does.
 *
 * Three states, never two. A measurement that failed has no share read at all - it is "not
 * measured", not "0% read" and never "partly read".
 */
export interface MeasuredBackup {
    file: string
    state: 'complete' | 'partly' | 'failed'
    /** Share of the file read, 0-100. Only when partly read and the scanner gave it. */
    percentRead: number | null
    /** Why it could not be measured, in the scanner's words. */
    error: string | null
    /** The scanner's sentence, e.g. "every byte measured: 12% random, 88% readable ...". */
    integrity: string | null
    /** What the format check does not prove - the scanner's own list. */
    notVerified: string[]
    /** Where the file does not match its format (a finding is raised for these as well). */
    problems: string[]
}

const isObject = (v: any) => !!v && typeof v === 'object' && !Array.isArray(v)
const textList = (v: any): string[] => (Array.isArray(v) ? v.map((x) => String(x ?? '').trim()).filter(Boolean) : [])

export function measuredBackups(data: any): MeasuredBackup[] {
    const cov = data?.coverage
    const rows: any[] = Array.isArray(cov?.volumes) ? cov.volumes : Array.isArray(cov?.per_volume) ? cov.per_volume : []
    return rows
        .filter((v) => v && (isObject(v.measured) || isObject(v.format_check)))
        .map((v) => {
            const measured = isObject(v.measured) ? v.measured : null
            const format = isObject(v.format_check) ? v.format_check : null
            const complete = (measured ?? format).complete === true
            const rawFraction = measured ? measured.fraction_read : format.figures?.fraction_read
            const fraction = rawFraction == null ? NaN : Number(rawFraction)
            const state: MeasuredBackup['state'] = complete ? 'complete' : Number.isFinite(fraction) ? 'partly' : 'failed'
            return {
                file: String(v.device || v.label || ''),
                state,
                percentRead: state === 'partly' ? Math.round(Math.min(Math.max(fraction, 0), 1) * 100) : null,
                error: measured && typeof measured.error === 'string' && measured.error.trim() ? measured.error.trim() : null,
                integrity: typeof v.integrity === 'string' && v.integrity.trim() ? v.integrity.trim() : null,
                notVerified: textList(format?.not_verified),
                problems: textList(format?.problems),
            }
        })
}
